// Fenêtre de configuration de l'exonération d'accise d'un PDL (art. L312-31 CIBS).
// Même pattern que PdlSiretEditorModal : écriture directe supabase.from('pdl') (RLS rang 0/8),
// toute erreur lue, affichée (toast) et loguée ; la fenêtre reste alors ouverte.
// Motif : E04 seul pour l'instant (enum extensible, contrainte pdl_exoneration_motif_chk).
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiX, FiSave } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";

const MOTIFS_EXONERATION = [
  { code: "E04", libelle: "E04 — Électricité consommée pour la production de produits énergétiques (méthanisation…)" },
];

export default function PdlExonerationEditorModal({ open, onClose, pdl, onSaved }) {
  const [motif, setMotif] = useState("");
  const [pourcentage, setPourcentage] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateResiliation, setDateResiliation] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMotif(pdl?.exoneration_motif || "");
    setPourcentage(pdl?.exoneration_pourcentage != null ? String(Number(pdl.exoneration_pourcentage)) : "");
    setDateDebut(pdl?.exoneration_date_debut || "");
    setDateResiliation(pdl?.date_resiliation || "");
  }, [open, pdl?.id, pdl?.exoneration_motif, pdl?.exoneration_pourcentage, pdl?.exoneration_date_debut, pdl?.date_resiliation]);

  // De 0,5 à 100, 2 décimales au plus (3310-TIC ligne H ; l'ERA l'arrondit à l'entier, « 0% » sous 0,5). La base
  // (NUMERIC(5,2)) arrondirait 80,004 en 80 sans rien dire : on refuse ici une 3e décimale.
  const pctSaisi = String(pourcentage).trim();
  const pct = /^\d{1,3}([.,]\d{1,2})?$/.test(pctSaisi) ? Number(pctSaisi.replace(",", ".")) : NaN;
  const pctValide = Number.isFinite(pct) && pct >= 0.5 && pct <= 100;
  const canSave = !saving && !!pdl?.id && (!motif || pctValide);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Motif vidé (« Aucune exonération ») : les 3 colonnes repassent à null
      // (contrainte pdl_exoneration_coherence_chk). Date vide → null, jamais ''.
      const payload = motif
        ? { exoneration_motif: motif, exoneration_pourcentage: pct, exoneration_date_debut: dateDebut || null }
        : { exoneration_motif: null, exoneration_pourcentage: null, exoneration_date_debut: null };
      // Date de résiliation (indépendante du motif) : envoyée seulement si elle a changé, pour que
      // la fenêtre reste utilisable sur une base sans la colonne (migration 033 non appliquée).
      if ((dateResiliation || null) !== (pdl.date_resiliation || null)) {
        payload.date_resiliation = dateResiliation || null;
      }
      const { data, error } = await supabase.from("pdl").update(payload).eq("id", pdl.id).select("id");
      if (error) throw error;
      // RLS : une mise à jour refusée ne renvoie pas d'erreur, juste 0 ligne.
      if (!data || data.length === 0) throw new Error("aucune ligne mise à jour (droits insuffisants ou PDL introuvable)");
      toast.success("Configuration du PDL enregistrée");
      if (onSaved) onSaved({ ...pdl, ...payload });
      onClose();
    } catch (e) {
      console.error("[PdlExonerationEditorModal] save error:", e);
      // Base sans migration 033 : contrainte « entier » (23514) ou colonne inconnue (PGRST204).
      const cause = e?.code === "23514" ? "pourcentage à décimales refusé par la base (migration 033 non appliquée)"
        : e?.code === "PGRST204" ? "date de résiliation inconnue de la base (migration 033 non appliquée)"
        : (e?.message || "enregistrement impossible");
      toast.error("Erreur : " + cause);
    } finally {
      setSaving(false);
    }
  };

  if (!open || !pdl) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" data-testid="modal-exoneration">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-xs font-semibold text-gray-800">Exonération d'accise (art. L312-31 CIBS)</h2>
            <p className="text-[10px] text-gray-500 mt-0.5">PDL <span className="font-mono">{pdl.pdl_number}</span> — {pdl.site_name || "—"}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 -mt-1" aria-label="Fermer"><FiX className="h-5 w-5" /></button>
        </div>

        <div className="px-4 py-4 space-y-3">
          <div>
            <label htmlFor="exo-motif" className="block text-xs font-medium text-gray-700 mb-1">Motif</label>
            <select id="exo-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">Aucune exonération (tarif réduit électro-intensif)</option>
              {MOTIFS_EXONERATION.map((m) => <option key={m.code} value={m.code}>{m.libelle}</option>)}
            </select>
          </div>
          {motif && (
            <>
              <div>
                <label htmlFor="exo-pct" className="block text-xs font-medium text-gray-700 mb-1">Part exonérée (%)</label>
                <input id="exo-pct" type="text" value={pourcentage}
                  onChange={(e) => setPourcentage(e.target.value)} inputMode="decimal" placeholder="ex. 82,35"
                  className={`w-full px-2 py-1 border rounded text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500 ${pctValide || pctSaisi === "" ? "border-gray-300" : "border-red-300"}`} />
                {!pctValide && pctSaisi !== "" && <p className="text-[10px] text-red-600 mt-1">Nombre de 0,5 à 100, 2 décimales au plus.</p>}
              </div>
              <div>
                <label htmlFor="exo-date" className="block text-xs font-medium text-gray-700 mb-1">Date d'effet <span className="text-gray-400 font-normal">(optionnelle)</span></label>
                <input id="exo-date" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
            </>
          )}
          <div>
            <label htmlFor="pdl-date-resiliation" className="block text-xs font-medium text-gray-700 mb-1">Date de résiliation du compteur <span className="text-gray-400 font-normal">(optionnelle)</span></label>
            <input id="pdl-date-resiliation" type="date" value={dateResiliation} onChange={(e) => setDateResiliation(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <p className="text-[10px] text-gray-500 mt-1">Compteur résilié pendant l'exercice : l'ERA s'arrête à la dernière date facturée au lieu de la clôture.</p>
          </div>
          <div className="text-[10px] text-gray-500 leading-snug">
            La part non exonérée est taxée au tarif normal (feuillets 1/2 de l'ERA). La consommation antérieure
            à la date d'effet est proratisée au jour. Un PDL exonéré se génère sans liasse ni TIC-VA.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-slate-50 rounded-b-lg">
          <button onClick={onClose} className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900">Annuler</button>
          <button onClick={handleSave} disabled={!canSave} data-testid="exo-save"
            className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded text-white ${canSave ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-300 cursor-not-allowed"}`}>
            <FiSave className="h-4 w-4 mr-1.5" />{saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
